package decoders

import ByteWitch
import bitmage.ByteOrder
import bitmage.fromBytes
import bitmage.fromIndex
import bitmage.untilIndex
import veryLargeDataFieldThreshold

object TLV8 : ByteWitchDecoder {
    override val name = "tlv8"

    override fun decode(data: ByteArray, sourceOffset: Int, inlineDisplay: Boolean): ByteWitchResult {
        val tlvs = mutableListOf<TlvChainEntry>()
        var remainder = data
        var internalOffset = 0
        val strict = data.size > veryLargeDataFieldThreshold

        while(remainder.isNotEmpty()) {
            if(remainder.size < 2)
                throw Exception("Insufficient TLV8 header bytes remaining")

            val type = remainder[0].toUByte().toInt()
            val length = remainder[1].toUByte().toInt()

            check(length <= remainder.size-2) { "Trying to read TLV8 of length $length with only ${remainder.size-2} bytes remaining" }
            check(!strict || (type < 128 && length > 0)) // reject likely incorrect parses earlier for very large payloads

            if(length > remainder.size-2)
                throw Exception("Trying to read TLV8 of length $length with only ${remainder.size-2} bytes remaining")
            val value = remainder.sliceArray(2 until 2+length)
            remainder = remainder.fromIndex(2+length)
            tlvs.add(TlvChainEntry(type, length, value, Pair(sourceOffset+internalOffset, sourceOffset+internalOffset+2+length)))
            internalOffset += 2+length
        }

        return TlvChainResult(tlvs, Pair(sourceOffset, sourceOffset+data.size))
    }

    override fun confidence(data: ByteArray, sourceOffset: Int): Pair<Double, ByteWitchResult?> {
        try {
            val res = decode(data, sourceOffset) as TlvChainResult

            val zeroLengthTLVs = res.elements.count { (it as TlvChainEntry).length == 0 }
            val zeroLengthPenalty = (zeroLengthTLVs.toDouble() / res.elements.size) * 0.8

            return Pair(1.0 - zeroLengthPenalty, res)
        } catch (e: Exception) {
            return Pair(0.0, null)
        }
    }
}

object TLV816 : ByteWitchDecoder {
    override val name = "tlv816"

    override fun decode(data: ByteArray, sourceOffset: Int, inlineDisplay: Boolean): ByteWitchResult {
        val tlvs = mutableListOf<TlvChainEntry>()
        var remainder = data
        var internalOffset = 0
        val strict = data.size > veryLargeDataFieldThreshold

        while(remainder.isNotEmpty()) {
            check(remainder.size >= 3) { "TLV816: insufficient header bytes remaining" }

            val type = remainder[0].toUByte().toInt()
            val length = Int.fromBytes(remainder.sliceArray(1 ..2), ByteOrder.BIG)

            check(length <= remainder.size-3) { "Trying to read TLV816 of length $length with only ${remainder.size-3} bytes remaining" }
            check(!strict || (type < 128 && length > 0)) // reject likely incorrect parses earlier for very large payloads

            val value = remainder.sliceArray(3 until 3+length)
            remainder = remainder.fromIndex(3+length)
            tlvs.add(TlvChainEntry(type, length, value, Pair(sourceOffset+internalOffset, sourceOffset+internalOffset+3+length), lengthLength = 2))
            internalOffset += 3+length
        }

        return TlvChainResult(tlvs, Pair(sourceOffset, sourceOffset+data.size))
    }

    override fun confidence(data: ByteArray, sourceOffset: Int): Pair<Double, ByteWitchResult?> {
        try {
            val res = decode(data, sourceOffset) as TlvChainResult

            val zeroLengthTLVs = res.elements.count { (it as TlvChainEntry).length == 0 }
            val zeroLengthPenalty = (zeroLengthTLVs.toDouble() / res.elements.size) * 0.8

            return Pair(1.0 - zeroLengthPenalty, res)
        } catch (e: Exception) {
            return Pair(0.0, null)
        }
    }
}

object TLV16 : ByteWitchDecoder {
    override val name = "tlv16"

    override fun decode(data: ByteArray, sourceOffset: Int, inlineDisplay: Boolean): ByteWitchResult {
        val tlvs = mutableListOf<TlvChainEntry>()
        var remainder = data
        var internalOffset = 0
        val strict = data.size > veryLargeDataFieldThreshold

        while(remainder.isNotEmpty()) {
            if(remainder.size < 4)
                throw Exception("Insufficient TLV16 header bytes remaining")

            val type = Int.fromBytes(remainder.untilIndex(2), ByteOrder.BIG, explicitlySigned = false)
            val length = Int.fromBytes(remainder.sliceArray(2 ..3), ByteOrder.BIG)

            check(length <= remainder.size-4) { "Trying to read TLV16 of length $length with only ${remainder.size-4} bytes remaining" }
            check(!strict || (type < 200 && length > 0)) // reject likely incorrect parses earlier for very large payloads

            val value = remainder.sliceArray(4 until 4+length)
            remainder = remainder.fromIndex(4+length)
            tlvs.add(TlvChainEntry(type, length, value, Pair(sourceOffset+internalOffset, sourceOffset+internalOffset+4+length), lengthLength = 2, typeLength = 2))
            internalOffset += 4+length
        }

        return TlvChainResult(tlvs, Pair(sourceOffset, sourceOffset+data.size))
    }

    override fun confidence(data: ByteArray, sourceOffset: Int): Pair<Double, ByteWitchResult?> {
        try {
            val res = decode(data, sourceOffset) as TlvChainResult

            val zeroLengthTLVs = res.elements.count { (it as TlvChainEntry).length == 0 }
            val zeroLengthPenalty = (zeroLengthTLVs.toDouble() / res.elements.size) * 0.8

            return Pair(1.0 - zeroLengthPenalty, res)
        } catch (e: Exception) {
            return Pair(0.0, null)
        }
    }
}

class TlvChainResult(tlvs: List<TlvChainEntry>, sourceByteRange: Pair<Int, Int>): BWGenericSequence(tlvs, sourceByteRange)

class TlvChainEntry(val type: Int, val length: Int, val value: ByteArray, override val sourceByteRange: Pair<Int, Int>, private val typeLength: Int = 1, private val lengthLength: Int = 1) : ByteWitchResult {
    override val colour = ByteWitchResult.Colour.GENERIC
    override fun renderHTML(): String {
        val parseAttempt = ByteWitch.quickDecode(value, sourceByteRange.second - value.size)
        val valueHTML = wrapIfSameColour(parseAttempt, value, relativeRangeTags(typeLength+lengthLength, value.size))
        return "<div class=\"roundbox generic\" $byteRangeDataTags><div class=\"bwvalue\" ${relativeRangeTags(0, typeLength)}>Type 0x${type.toString(16)}</div><div class=\"bpvalue\" ${relativeRangeTags(typeLength, lengthLength)}>Len: ${humanReadableByteCount(length)}</div>$valueHTML</div>"
    }
}